<template>
  <v-table :mobile="xs">
    <thead v-if="!xs">
      <tr>
        <th style="width: 260px">
          {{ t('global.name') }}
        </th>
        <th style="width: 180px">
          {{ t('role.title') }}
        </th>
        <th>{{ t('global.description') }}</th>
        <th>{{ t('team.primary_color') }}</th>
        <th>
          {{ t('actions.delete') }}
        </th>
      </tr>
    </thead>
    <tbody>
      <tr
        v-for="(member, index) in members"
        :key="index"
      >
        <td>
          <v-autocomplete
            v-model="member.id"
            :label="t('member.title')"
            :items="users"
            item-title="name"
            item-value="id"
            hide-details
            :return-object="false"
            class="mb-margin"
          />
        </td>
        <td>
          <v-combobox
            v-model="member.pivot.role"
            :items="roles"
            :label="t('role.title')"
            chips
            class="mb-margin mt-8"
          />
        </td>
        <td>
          <v-textarea
            v-model="member.pivot.role_description"
            hide-details
            auto-grow
            rows="1"
            class="mb-margin"
          />
        </td>
        <td style="width: 30px">
          <v-checkbox
            v-model="member.pivot.primary_color"
          />
        </td>
        <td>
          <v-btn
            :disabled="index <= 0"
            class="me-1 my-1"
            color="secondary"
            size="small"
            @click="moveUp(index)"
          >
            <v-icon icon="fas fa-arrow-up" />
          </v-btn>
          <v-btn
            :disabled="index >= (members.length - 1)"
            class="me-1 my-1"
            color="secondary"
            size="small"
            @click="moveDown(index)"
          >
            <v-icon icon="fas fa-arrow-down" />
          </v-btn>
          <v-btn
            color="error"
            class="mb-margin"
            size="small"
            @click="deleteItem(index)"
          >
            <v-icon icon="fas fa-trash" />
          </v-btn>
        </td>
      </tr>
      <tr>
        <td>
          <v-autocomplete
            v-model="newUser"
            :label="t('member.title')"
            :items="users"
            item-title="name"
            item-value="id"
            hide-details
            :return-object="true"
            clear-on-select
            class="mb-margin"
          />
        </td>
        <td />
        <td />
      </tr>
    </tbody>
  </v-table>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDisplay } from 'vuetify';
import { useAdminStore } from '@/stores/admin';

const { xs } = useDisplay();
const { t } = useI18n();
const members = defineModel<Array<App.DataTables.UserWithPivot>>({
  default: () => [],
});

const newUser = ref<App.DataTables.UserWithPivot>();

function moveItem(from: number, to: number): void {
  members.value.splice(to, 0, members.value.splice(from, 1)[0]);
  members.value.forEach((member, index) => {
    member.pivot.order = index;
  });
}
function moveUp(index: number): void {
  if (index > 0) {
    moveItem(index, index - 1);
  }
}
function moveDown(index: number): void {
  if (index < members.value.length - 1) {
    moveItem(index, index + 1);
  }
}

const roles = ref([
  t('member.title'),
  t('global.assistant'),
]);

function newMemberSelected() {
  if (newUser.value) {
    newUser.value.pivot = {
      role: t('member.title'),
      role_description: '',
      order: members.value.length,
      primary_color: false,
    };
    members.value.push(newUser.value);
    newUser.value = undefined;
  }
}

function deleteItem(index: number): void {
  members.value.splice(index, 1);
}

const adminStore = useAdminStore();
const { users } = storeToRefs(adminStore);
watch(newUser, () => {
  newMemberSelected();
});
</script>

<style scoped>
@media (max-width: 767px){
  tr,td{
    display: block;
  }
  .mb-margin {
    margin-top: 8px !important;
  }
}
</style>
